import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useQuery } from "@apollo/client";
import classNames from "classnames";
import { FormikProvider, useFormik } from "formik";
import { capitalize, sortBy } from "lodash";
import { Col, Form, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { regionsKeys } from "components/calculations/utils/regionNames";
import Button from "components/common/Button";
import Loading from "components/common/Loading";
import SelectField from "components/common/inputs/Select/SelectField";
import TaxYearSelect from "components/common/inputs/Select/TaxYearSelect";
import TextInput from "components/common/inputs/TextInput";
import ToggleCheckbox from "components/common/inputs/ToggleCheckbox";
import { CONFIRM_CREATE_WITH_MISSING_MODAL_NAME } from "components/modals/documents/ConfirmCreateWithMissingModal";

import useCourtInformation from "hooks/me/useCourtInformation";
import useCreateDocumentFlow from "hooks/useCreateDocumentFlow";
import useModal from "hooks/useModal";

import { getOptions } from "helpers/documentForm";

import { formatDate } from "utils/date";
import getVariablesForJurisdictionQuery from "utils/jurisdiction/getVariablesForJurisdictionQuery";
import getVariablesForAllTemplatesQuery from "utils/templates/getVariablesForAllTemplatesQuery";

import useCreateDocument from "@gql/hooks/documents/useCreateDocument";
import useMissingNormalizedPaths from "@gql/hooks/useMissingNormalizedPaths";
import ALL_COURTS from "@gql/sanity/allCourts";
import ALL_JURISDICTIONS from "@gql/sanity/allJurisdictions";
import ALL_TEMPLATES from "@gql/sanity/allTemplates";

const CreateDocumentForm = ({
  setJurisdiction,
  isShowJurisdiction,
  setIsShowJurisdiction,
  inPageFlow = false,
  onDocumentChange,
  onTaxYearChange,
  optionsOverride,
  selectedSections,
  rightFooter,
  onDocumentSettingsChange,
  expandSignal,
  formCompletenessPercent,
  remoteSigningAvailable,
  remoteSigningEnabled,
  onRemoteSigningChange,
  onRequestExpandWizard,
  showInlineCompleteButton = true,
  wizardStats,
  wizardMissingSteps,
  onWizardStart,
}) => {
  const { options, close } = useModal("CREATE_DOCUMENT");
  const effectiveOptions = optionsOverride || options;

  const [document, setDocument] = useState(null);
  const [fieldsSecurity, setFieldsSecurity] = useState("LOCKED");
  const [documentLoading, setDocumentLoading] = useState(false);
  const [isManageSections, setIsManageSections] = useState(false);
  const [taxYear, setTaxYear] = useState(new Date().getFullYear().toString());
  const [openInWord, setOpenInWord] = useState(true);
  const [autoExpand, setAutoExpand] = useState(false);

  const navigate = useNavigate();
  const searchInputRef = useRef();
  const rightColRef = useRef();
  const handleScrollToWizard = useCallback(() => {
    try {
      const el = window.document.getElementById("form-wizard");
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    } catch (err) {
      console.warn("Failed to scroll to form wizard:", err);
    }
  }, []);

  const scrollToFirstMissingField = useCallback(() => {
    try {
      const container = window.document.querySelector(".dynamic-form-editor");
      if (!container) return;
      const first = container.querySelector(".is-missing");
      if (first && typeof first.scrollIntoView === "function") {
        first.scrollIntoView({ behavior: "smooth", block: "center" });
        if (typeof first.focus === "function") first.focus();
      }
    } catch (err) {
      console.warn("Failed to scroll to first missing field:", err);
    }
  }, []);

  // Keep the left templates list compact initially, but allow it to grow if the right column expands
  const MIN_LIST_HEIGHT = 260;
  const MAX_LIST_HEIGHT = 320;
  const [templatesListMaxHeight, setTemplatesListMaxHeight] = useState(MIN_LIST_HEIGHT);

  // Persist openInWord and last-used location
  useEffect(() => {
    const savedOpen = localStorage.getItem("dp_open_in_word");
    if (savedOpen != null) setOpenInWord(savedOpen === "true");
  }, []);
  useEffect(() => {
    localStorage.setItem("dp_open_in_word", String(openInWord));
  }, [openInWord]);

  const { client } = effectiveOptions || {};

  // Missing stats for confirmation modal
  const missing = useMissingNormalizedPaths({
    clientId: client?.id,
    paths: document?.normalized_paths || [],
    taxYear,
    ignoreIfAbsent: [
      "client.parties",
      "client.children",
      "client.profile.middleName",
      "client.exProfile.middleName",
      "client.address.street2",
      "client.exAddress.street2",
    ],
  });
  const summary = missing?.data?.missingNormalizedPaths?.summary;
  const missingGroups = useMemo(
    () => missing?.data?.missingNormalizedPaths?.groups || [],
    [missing?.data?.missingNormalizedPaths?.groups],
  );

  const { open: openClientSettingsModal } = useModal("CLIENT_DOCUMENT_SETTINGS");
  const { provinceShorthand: residence, courtId, courtFile } = useCourtInformation(client);

  const documentTitle = document?.name?.replace(/(.*): /, "");

  const clientType = useMemo(() => {
    const isPlaintiff = client?.isPlaintiff ? "Plaintiff" : "Defendant";
    const isApplicant = client?.isApplicant ? "Applicant" : "Respondent";
    const clientLabel = client?.clientLabel;

    return `${capitalize(clientLabel)} is ${isPlaintiff}/${isApplicant}`;
  }, [client]);

  /* ===================
    Jurisdictions
    ================== */
  const { data, loading: jurisdictionLoading } = useQuery(ALL_JURISDICTIONS, {
    variables: getVariablesForJurisdictionQuery(),
  });

  const allJurisdictions = useMemo(() => {
    const jurisdictions = [];

    if (data?.allCountry) {
      jurisdictions.push(...data.allCountry);
    }

    if (data?.allProvince) {
      jurisdictions.push(...data.allProvince);
    }

    return jurisdictions;
  }, [data]);

  const defaultJurisdictionShorthand = useMemo(() => {
    const jurisdiction = options?.defaultJurisdiction || residence;

    const existingJurisdiction = allJurisdictions?.find(
      (jurisdictionEl) => jurisdiction === jurisdictionEl?.shorthand,
    );

    return existingJurisdiction?.shorthand;
  }, [allJurisdictions, options?.defaultJurisdiction, residence]);

  /* ===============
    Form Submit
    ============= */
  const [createDocument, { loading }] = useCreateDocument({ client });

  const createDocumentFlow = useCreateDocumentFlow(0, { client });

  // Notify parent when selected document changes (for page flow composition)
  useEffect(() => {
    if (onDocumentChange) onDocumentChange(document);
  }, [document, onDocumentChange]);

  // Notify parent when tax year changes (for page-level calculation modal)
  useEffect(() => {
    if (onTaxYearChange) onTaxYearChange(taxYear);
  }, [taxYear, onTaxYearChange]);

  const handleCreateDocument = useCallback(
    async (sections) => {
      if (loading) return null;

      const toastId = toast.loading("Creating new document...");

      try {
        setDocumentLoading(true);

        const result = await createDocument({
          variables: {
            data: {
              templateId: document?._id,
              clientId: client?.id,
              taxYear: +taxYear,
              sections: sections?.length ? sections : undefined,
              fieldsSecurity,
            },
          },
        });

        const createdId = result?.data?.createDocument?.id;

        if (effectiveOptions?.shouldNavigateToClient || inPageFlow) {
          navigate(`/clients/${client?.id}/documents`);
        }

        effectiveOptions?.onDocumentCreate?.();

        // Optionally open in Word Online in a new tab
        if (inPageFlow && openInWord && createdId) {
          const base = import.meta.env.BASE_URL || "/";
          const path = `${base.replace(/\/$/, "")}/wopi/${createdId}`;
          window.open(path, "_blank", "noopener,noreferrer");
        }

        toast.success("Document successfully created!");
      } catch (err) {
        console.error(err);
        toast.error("Couldn't create document");
      } finally {
        toast.dismiss(toastId);
        setDocumentLoading(false);
        if (!inPageFlow) {
          close();
        }
      }
    },
    [
      effectiveOptions,
      inPageFlow,
      openInWord,
      setDocumentLoading,
      client?.id,
      close,
      createDocument,
      document?._id,
      navigate,
      loading,
      taxYear,
      fieldsSecurity,
    ],
  );

  const { open: openConfirm } = useModal(CONFIRM_CREATE_WITH_MISSING_MODAL_NAME);

  const onSubmit = useCallback(async () => {
    const percent =
      wizardStats?.percent != null
        ? wizardStats.percent
        : summary?.presentCompletenessPercent ?? 100;
    if (inPageFlow && percent < 100) {
      openConfirm({
        clientId: client?.id,
        summary,
        groups: missingGroups,
        wizardStats,
        wizardMissingSteps,
        onConfirm: async () => {
          await handleCreateDocument(selectedSections);
        },
        onClose: undefined,
        onStartWizard: () => {
          if (typeof onWizardStart === "function") onWizardStart();
        },
        scrollToWizardPath: (path) => {
          if (typeof onWizardStart === "function") onWizardStart();
          // After wizard opens on the page, scroll to the targeted path
          setTimeout(() => {
            try {
              const event = new CustomEvent("dp-scroll-to-missing", { detail: { path } });
              window.dispatchEvent(event);
            } catch (err) {
              console.warn("Failed to dispatch scroll-to-missing event:", err);
            }
          }, 0);
        },
      });
      return;
    }
    if (inPageFlow) {
      // In page flow, create the document directly, including selected sections (if any)
      await handleCreateDocument(selectedSections);
      return;
    }
    createDocumentFlow({
      client,
      document,
      submit: handleCreateDocument,
      isManageSections,
    });

    close();
  }, [
    openConfirm,
    wizardStats,
    summary,
    missingGroups,
    wizardMissingSteps,
    onWizardStart,
    client,
    document,
    createDocumentFlow,
    handleCreateDocument,
    close,
    isManageSections,
    inPageFlow,
    selectedSections,
  ]);

  /* ===============
    Initialize Form
    =========== */
  const formik = useFormik({
    initialValues: {
      jurisdiction: defaultJurisdictionShorthand || "",
      document: "",
      court: effectiveOptions?.defaultCourt || courtId || "ANY",
      search:
        effectiveOptions?.defaultSearch !== undefined
          ? effectiveOptions.defaultSearch
          : localStorage.getItem("dp_doc_search") || "",
    },
    validateOnBlur: false,
    enableReinitialize: true,
  });

  const { setFieldValue, values } = formik;
  const { jurisdiction, court, search } = values;

  useEffect(() => {
    localStorage.setItem("dp_doc_search", search || "");
  }, [search]);

  /* =================
    Courts
    ============= */
  const { data: courtsData, loading: courtsLoading } = useQuery(ALL_COURTS, {
    variables: { where: { province: { shorthand: { eq: jurisdiction } } } },
    skip: !jurisdiction,
  });

  const courtLocation = useMemo(
    () =>
      courtsData?.allCourt
        ?.find((court) => court?._id === courtId)
        ?.locations?.find(({ _id }) => _id === client?.court?.courtLocationId)?.name || "",
    [courtsData?.allCourt, courtId, client?.court?.courtLocationId],
  );

  /* =================
    Templates
    ============= */
  const { loading: templateLoading, data: dataTemplates } = useQuery(ALL_TEMPLATES, {
    variables: getVariablesForAllTemplatesQuery({
      category: effectiveOptions?.defaultCategory,
      courts: courtsData?.allCourt,
    }),
  });

  const filteredTemplates = useMemo(() => {
    return dataTemplates?.allDocumentTemplate?.filter((template) => {
      const templateCategoryName = template?.category?.name || "";

      const isCourtExist =
        court === "ANY" ||
        template?.courts?.find((tCourt) => tCourt._id === court) ||
        template?.court?._id === court;

      const isInJurisdiction = template?.jurisdiction?.shorthand === jurisdiction;

      const isInCategory = templateCategoryName.includes(effectiveOptions?.defaultCategory || "");

      if (isInJurisdiction && isCourtExist && isInCategory) {
        const templateName = template?.name?.toLowerCase() || "";
        const templateNumber = template?.number?.toLowerCase() || "";
        const searchValue = search.toLowerCase();

        return templateName.includes(searchValue) || templateNumber.includes(searchValue);
      }

      return false;
    });
  }, [
    dataTemplates?.allDocumentTemplate,
    effectiveOptions?.defaultCategory,
    jurisdiction,
    court,
    search,
  ]);

  const sortedTemplates = useMemo(() => {
    return filteredTemplates
      ?.slice()
      .sort((a, b) => a?.number?.localeCompare(b?.number, "en", { numeric: true }));
  }, [filteredTemplates]);

  const emptyTemplatesInfo = useMemo(() => {
    if (sortedTemplates?.length) {
      return null;
    }

    if (templateLoading || courtsLoading) return <Loading />;

    if (!jurisdiction && !court) {
      return <span className="no-templates">Select jurisdiction</span>;
    }

    if (jurisdiction && !court) {
      return <span className="no-templates">Select court</span>;
    }

    if (templateLoading === false && !sortedTemplates?.length) {
      return <span className="no-templates">More forms and precedents are coming soon...</span>;
    }
  }, [sortedTemplates?.length, templateLoading, courtsLoading, jurisdiction, court]);

  // Move the setState logic to a useEffect to avoid setState during render
  useEffect(() => {
    if (sortedTemplates?.length) {
      setIsShowJurisdiction(false);
    } else if (!jurisdiction && !court) {
      setIsShowJurisdiction(true);
    }
  }, [sortedTemplates?.length, jurisdiction, court, setIsShowJurisdiction]);

  /* ==================
    Select Options
    ================= */
  const jurisdictionOptions = useMemo(() => {
    return sortBy(
      allJurisdictions.map((jurisdiction) => ({
        label: jurisdiction.name,
        value: jurisdiction.shorthand,
      })),
      "label",
    );
  }, [allJurisdictions]);

  // Notify parent of document settings snapshot (for page layout row)
  useEffect(() => {
    if (!onDocumentSettingsChange) return;
    const jurisdictionLabel = (jurisdictionOptions || []).find(
      (j) => j.value === jurisdiction,
    )?.label;
    onDocumentSettingsChange({
      jurisdictionShorthand: jurisdiction,
      jurisdictionLabel: jurisdictionLabel || "—",
      taxYear,
      courtLocationLabel: courtLocation,
      courtFile: client?.court?.courtFile || "—",
      clientType,
      fieldsSecurity,
    });
  }, [
    jurisdiction,
    jurisdictionOptions,
    taxYear,
    courtLocation,
    client?.court?.courtFile,
    clientType,
    fieldsSecurity,
    onDocumentSettingsChange,
  ]);

  const courtOptions = useMemo(() => {
    return [
      { value: "ANY", label: "Any Court" },
      ...(sortBy(getOptions(courtsData?.allCourt), "label") || []),
    ];
  }, [courtsData?.allCourt]);

  const courtLocationLabel = `${courtLocation || ""}${courtLocation && courtFile ? " – " : ""}${
    courtFile || ""
  }`;

  useEffect(() => {
    // Set Jurisdiction for Modal's header
    setJurisdiction(regionsKeys[jurisdiction]);

    const existingDefaultCourt = courtsData?.allCourt?.find((court) => court._id === courtId);

    setFieldValue("court", existingDefaultCourt?._id || "ANY");
    // persist last used
    if (jurisdiction) localStorage.setItem("dp_last_jurisdiction", jurisdiction);
    if (existingDefaultCourt?._id || court) {
      localStorage.setItem("dp_last_court", existingDefaultCourt?._id || court);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jurisdiction, courtsData, courtId]);

  const filteredSections = document?.sections?.filter(Boolean);

  useEffect(() => {
    setIsManageSections(!!filteredSections?.length);
  }, [filteredSections?.length]);

  useEffect(() => {
    if (sortedTemplates?.length && !sortedTemplates?.find((f) => f._id === document?._id)) {
      setDocument(sortedTemplates[0]);
    }

    if (!sortedTemplates?.length) {
      setDocument(null);
    }
  }, [sortedTemplates, document, templateLoading]);

  useEffect(() => {
    if (!jurisdiction && !defaultJurisdictionShorthand) {
      setIsShowJurisdiction(true);
    }
  }, [jurisdiction, defaultJurisdictionShorthand, setIsShowJurisdiction]);

  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  const measureRightColHeight = () => {
    const el = rightColRef.current;
    if (!el) return;
    try {
      const rect = el.getBoundingClientRect();
      const next = Math.min(
        MAX_LIST_HEIGHT,
        Math.max(MIN_LIST_HEIGHT, Math.round(rect?.height || MIN_LIST_HEIGHT)),
      );
      setTemplatesListMaxHeight(next);
    } catch (err) {
      console.warn("Failed to measure right column height:", err);
      // Fall back to minimum height on error
      setTemplatesListMaxHeight(MIN_LIST_HEIGHT);
    }
  };

  // Observe right column height only after an explicit expand signal
  useEffect(() => {
    if (!inPageFlow || !autoExpand) return undefined;
    const el = rightColRef.current;
    if (!el) return undefined;

    measureRightColHeight();

    let resizeObserver;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(measureRightColHeight);
      resizeObserver.observe(el);
    } else {
      window.addEventListener("resize", measureRightColHeight);
    }

    return () => {
      if (resizeObserver && el) resizeObserver.unobserve(el);
      if (!resizeObserver) window.removeEventListener("resize", measureRightColHeight);
    };
  }, [inPageFlow, autoExpand]);

  // External expand signal (e.g., from completeness panel 'Show all')
  useEffect(() => {
    if (expandSignal == null) return;
    if (expandSignal > 0) {
      setAutoExpand(true);
      setTimeout(measureRightColHeight, 0);
    } else {
      // collapse
      setAutoExpand(false);
      setTemplatesListMaxHeight(MIN_LIST_HEIGHT);
    }
  }, [expandSignal]);

  return (
    <FormikProvider value={formik}>
      <Form className="create-document-form">
        <Row>
          <Col sm={7} className="d-flex flex-column">
            {!inPageFlow && (
              <Row>
                {isShowJurisdiction && (
                  <Col sm={6} className="pr-sm-2">
                    <SelectField
                      placeholder="Select Jurisdiction"
                      options={jurisdictionOptions}
                      containerClassName="mb-2 mb-sm-3"
                      name="jurisdiction"
                      label="Jurisdiction"
                      isLoading={jurisdictionLoading}
                      isRequired
                    />
                  </Col>
                )}
                <Col
                  sm={isShowJurisdiction ? 6 : 12}
                  className={classNames({ "pl-sm-2": isShowJurisdiction })}
                >
                  <SelectField
                    placeholder="Select Court"
                    options={courtOptions}
                    containerClassName="mb-3"
                    value={court}
                    name="court"
                    label="Court"
                    isLoading={courtsLoading}
                    isRequired
                  />
                </Col>
              </Row>
            )}
            <Row>
              <Col sm={6} className="pr-sm-2">
                <TextInput
                  inputRef={searchInputRef}
                  containerClassName="mb-2 mb-sm-3"
                  name="search"
                  label="Search"
                  placeholder="Affidavit"
                  autoFocus
                  append="circle-cross"
                  appendOnClick={() => setFieldValue("search", "")}
                  isDebounced
                  debounceMs={250}
                />
              </Col>
              {!inPageFlow ? (
                <Col sm={6} className="pl-sm-2 mb-2">
                  <TaxYearSelect
                    label="Tax Year"
                    value={taxYear}
                    onChange={({ value }) => setTaxYear(value)}
                  />
                </Col>
              ) : (
                <Col sm={6} className="pl-sm-2 mb-2">
                  <SelectField
                    placeholder="Select Court"
                    options={courtOptions}
                    containerClassName="mb-2"
                    value={court}
                    name="court"
                    label="Court"
                    isLoading={courtsLoading}
                    isRequired
                  />
                </Col>
              )}
            </Row>
            <div
              className={classNames("templates-selector", {
                loading: templateLoading,
                empty: !templateLoading && !sortedTemplates?.length,
              })}
              role="listbox"
              aria-label="Templates"
              style={
                inPageFlow
                  ? {
                      minHeight: MIN_LIST_HEIGHT,
                      maxHeight: templatesListMaxHeight,
                      overflowY: "auto",
                    }
                  : undefined
              }
              tabIndex={0}
              onKeyDown={(e) => {
                if (!sortedTemplates?.length) return;
                const currentIndex = sortedTemplates.findIndex((t) => t?._id === document?._id);
                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  const next =
                    sortedTemplates[Math.min(currentIndex + 1, sortedTemplates.length - 1)];
                  if (next) setDocument(next);
                }
                if (e.key === "ArrowUp") {
                  e.preventDefault();
                  const prev = sortedTemplates[Math.max(currentIndex - 1, 0)];
                  if (prev) setDocument(prev);
                }
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (e.metaKey || e.ctrlKey) onSubmit();
                  else onSubmit();
                }
              }}
            >
              {emptyTemplatesInfo}
              {!templateLoading && !!sortedTemplates?.length && (
                <Fragment>
                  <div className="templates-selector-list">
                    {sortedTemplates?.map((template) => (
                      <div
                        className={classNames("template", {
                          active: template._id === document?._id,
                        })}
                        onClick={() => setDocument(template)}
                        onMouseEnter={() => {
                          // Prefetch stub: could prefetch additional metadata if needed
                        }}
                        key={template._id}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") setDocument(template);
                        }}
                      >
                        <div className="template-name">
                          {`${template.name}${template.deskdivorce ? " (Desk Application)" : ""}`}
                        </div>
                        <div className="template-number">{template.number}</div>
                      </div>
                    ))}
                  </div>
                  <div className="templates-selector-footer">
                    {sortedTemplates?.length} Documents
                  </div>
                </Fragment>
              )}
            </div>
          </Col>
          <Col sm={5} className="document-column">
            {document?._id && !!sortedTemplates?.length ? (
              <div
                ref={rightColRef}
                className="document-description-content"
                style={{ marginBottom: 8 }}
              >
                <h4 className="document-title" style={{ marginBottom: 4 }}>
                  {document?.number ? `${document?.number}: ` : ""}
                  {documentTitle}
                </h4>
                <span className="document-subtitle" style={{ marginBottom: 2 }}>
                  {document?.court?.name}
                </span>
                <span className="document-subtitle" style={{ marginBottom: 6 }}>
                  {courtLocationLabel}
                </span>
                {(document?.official_publication_date || document?.official_source_url) && (
                  <p className="revision-date" style={{ marginBottom: 6 }}>
                    {document?.official_publication_date && (
                      <Fragment>
                        <span className="date-label">Official Revision Date:</span>
                        <span className="date-value">
                          {formatDate({
                            date: document?.official_publication_date,
                            template: "MM.DD.YYYY",
                          })}
                        </span>
                      </Fragment>
                    )}
                    {document?.official_source_url && (
                      <a
                        href={document?.official_source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="document-link"
                        style={{ marginLeft: 6 }}
                      >
                        (source)
                      </a>
                    )}
                  </p>
                )}
                {!inPageFlow && <span className="document-subtitle">{clientType}</span>}
                {!inPageFlow && (
                  <span
                    className="change-settings"
                    onClick={() =>
                      openClientSettingsModal({
                        client,
                        jurisdiction,
                        courtId: courtId || court,
                        onSuccess: setFieldsSecurity,
                      })
                    }
                  >
                    Change Settings
                  </span>
                )}
                {!!filteredSections?.length && !inPageFlow && (
                  <div className="manage-sections custom-control custom-checkbox">
                    <input
                      className="custom-control-input"
                      name="manage-sections"
                      id="manage-sections"
                      onChange={() => setIsManageSections(!isManageSections)}
                      type="checkbox"
                      checked={isManageSections}
                    />
                    <label className="custom-control-label" htmlFor="manage-sections">
                      <span>Manage Sections</span>
                    </label>
                  </div>
                )}
                <div className="divider" style={{ margin: "8px 0 6px" }} />
                {/* Help link removed per UX update */}
                {inPageFlow && (
                  <Fragment>
                    <div
                      className="buttons d-flex mt-1 align-items-center flex-wrap"
                      style={{ gap: 10 }}
                    >
                      <Button
                        className="btn-shimmer-always"
                        disabled={!document?._id || documentLoading}
                        onClick={onSubmit}
                      >
                        {documentLoading ? "Creating Document..." : "Create"}
                      </Button>
                      <div
                        className="d-inline-flex align-items-center"
                        style={{
                          background: "#f8f9fa",
                          border: "1px solid #e9ecef",
                          borderRadius: 6,
                          padding: "0 10px",
                          whiteSpace: "nowrap",
                          height: 42,
                          lineHeight: 1,
                          gap: 8,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 12,
                            color: "#495057",
                            marginRight: 0,
                            lineHeight: 1,
                            display: "inline-flex",
                            alignItems: "center",
                          }}
                        >
                          Open in Word
                        </span>
                        <div
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            transform: "translateY(6px)",
                          }}
                        >
                          <ToggleCheckbox
                            className="toggle-checkbox-sm"
                            isChecked={openInWord}
                            onChange={(e) => setOpenInWord(e.target.checked)}
                          />
                        </div>
                      </div>
                      {remoteSigningAvailable && (
                        <div
                          className="d-inline-flex align-items-center"
                          style={{
                            background: "#f8f9fa",
                            border: "1px solid #e9ecef",
                            borderRadius: 6,
                            padding: "0 10px",
                            whiteSpace: "nowrap",
                            height: 42,
                            lineHeight: 1,
                            gap: 8,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 12,
                              color: "#495057",
                              marginRight: 0,
                              lineHeight: 1,
                              display: "inline-flex",
                              alignItems: "center",
                            }}
                          >
                            Remote Signing
                          </span>
                          <div
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              transform: "translateY(6px)",
                            }}
                          >
                            <ToggleCheckbox
                              className="toggle-checkbox-sm"
                              isChecked={!!remoteSigningEnabled}
                              onChange={(e) => onRemoteSigningChange?.(!!e.target.checked)}
                            />
                          </div>
                        </div>
                      )}
                      {showInlineCompleteButton && (formCompletenessPercent ?? 100) < 100 && (
                        <Button
                          className="btn-shimmer"
                          onClick={() => {
                            if (typeof onRequestExpandWizard === "function") {
                              onRequestExpandWizard();
                            }
                            handleScrollToWizard();
                            // Give the wizard a moment to expand and mark fields, then scroll
                            setTimeout(scrollToFirstMissingField, 600);
                          }}
                          style={{ backgroundColor: "#28a745", borderColor: "#28a745" }}
                        >
                          Complete
                        </Button>
                      )}
                    </div>
                    <div className="mt-2" />
                    {rightFooter}
                  </Fragment>
                )}
              </div>
            ) : (
              <div className="no-document">More forms coming soon</div>
            )}
            {!inPageFlow && (
              <div className="buttons d-flex">
                <Button
                  className="btn-shimmer"
                  disabled={!document?._id || documentLoading}
                  onClick={onSubmit}
                >
                  {documentLoading ? "Creating Document..." : "Create"}
                </Button>
                <Button variant="secondary" onClick={close}>
                  Cancel
                </Button>
              </div>
            )}
          </Col>
        </Row>
      </Form>
    </FormikProvider>
  );
};

export default CreateDocumentForm;
